
import { createContext, useContext, useState, useEffect, ReactNode, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type ServiceId = 
  | "nutrition-counselling" 
  | "gut-health" 
  | "weight-management" 
  | "sports-nutrition" 
  | "chronic-diseases" 
  | "lifecycle-nutrition";

interface ServiceContextType {
  activeService: ServiceId | null;
  setActiveService: (serviceId: ServiceId) => void;
  scrollToService: (serviceId: ServiceId) => void;
  isNavigatingMobile: boolean;
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

export const useService = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error("useService must be used within a ServiceProvider");
  }
  return context;
};

interface ServiceProviderProps {
  children: ReactNode;
}

export const ServiceProvider = ({ children }: ServiceProviderProps) => {
  const [activeService, setActiveService] = useState<ServiceId | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const initialRenderRef = useRef(true);
  const readyToScrollRef = useRef(false);
  const isNavigatingRef = useRef(false);
  const navigationInProgressRef = useRef<boolean>(false);
  const mobileNavigationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigationTargetRef = useRef<ServiceId | null>(null);
  const [isNavigatingMobile, setIsNavigatingMobile] = useState(false);

  // Clear navigation timers safely - new helper function
  const clearNavigationTimers = () => {
    if (mobileNavigationTimerRef.current) {
      clearTimeout(mobileNavigationTimerRef.current);
      mobileNavigationTimerRef.current = null;
      console.log("Navigation timer cleared");
    }
  };

  // Extract service ID from URL and set active service on initial load
  useEffect(() => {
    const path = location.pathname;
    
    console.log("Location changed:", path);
    
    // Don't clear navigation timers if we're actively navigating to a service
    // This prevents the timer being cleared before navigation completes
    if (!navigationTargetRef.current || path.includes(`/services/${navigationTargetRef.current}`)) {
      // Only clear navigation flags when navigation is complete
      isNavigatingRef.current = false;
      
      if (path.startsWith("/services/")) {
        const serviceId = path.split("/services/")[1] as ServiceId;
        setActiveService(serviceId);
        readyToScrollRef.current = true;
        
        // Only mark initial render as complete when on a specific service route
        if (initialRenderRef.current) {
          initialRenderRef.current = false;
        }
      } else if (path === "/services") {
        // Default to first service if none is specified
        if (!activeService) {
          setActiveService("nutrition-counselling");
        }
        
        // Mark initial render as complete
        if (initialRenderRef.current) {
          initialRenderRef.current = false;
        }
      } else {
        // Reset active service when not on services page
        setActiveService(null);
        initialRenderRef.current = true;
      }
      
      // When navigation is complete, reset the target and flags
      navigationTargetRef.current = null;
      navigationInProgressRef.current = false;
      setIsNavigatingMobile(false);
      clearNavigationTimers();
    } else {
      console.log("Preserving navigation state for target:", navigationTargetRef.current);
    }
  }, [location.pathname, activeService]);

  // Handle scrolling after components are mounted
  useEffect(() => {
    if (readyToScrollRef.current && activeService && location.pathname.includes('/services')) {
      console.log("Ready to scroll to:", activeService);
      
      // Wait longer for components to be fully rendered, especially after navigation
      const timer = setTimeout(() => {
        attemptToScrollToService(activeService);
        readyToScrollRef.current = false;
      }, 1000); // Increased timeout for better reliability after navigation
      
      return () => clearTimeout(timer);
    }
  }, [activeService, location.pathname]);

  // Helper function to attempt scrolling with retries
  const attemptToScrollToService = (serviceId: ServiceId, retryCount = 0) => {
    const element = document.getElementById(serviceId);
    
    if (element) {
      // Calculate header offset to ensure proper scrolling position
      const headerOffset = 80; // Approximate header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      
      console.log(`Scrolled to service: ${serviceId}`);
    } else {
      console.log(`Could not find element with id: ${serviceId}, retry: ${retryCount}`);
      
      // Retry a few times if element isn't found immediately
      if (retryCount < 5) {
        setTimeout(() => {
          attemptToScrollToService(serviceId, retryCount + 1);
        }, 300); // Retry after a short delay
      }
    }
  };

  const scrollToService = (serviceId: ServiceId) => {
    console.log(`Scroll request for: ${serviceId}, current path: ${location.pathname}`);
    
    // If navigation is already in progress, don't start another navigation
    if (navigationInProgressRef.current) {
      console.log("Navigation already in progress, waiting...");
      return;
    }
    
    // Set the active service
    setActiveService(serviceId);
    
    // Store the target service ID to preserve it during navigation
    navigationTargetRef.current = serviceId;
    
    // Check if we need to navigate
    const isOnServicesPage = location.pathname.includes('/services');
    
    if (!isOnServicesPage) {
      // We need to navigate first, then scroll after the page loads
      console.log(`Navigating to /services/${serviceId}`);
      navigationInProgressRef.current = true;
      
      // For mobile interactions, use a longer delay to ensure UI feedback
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setIsNavigatingMobile(true);
        
        // Clear any existing timers before setting new ones
        clearNavigationTimers();
        
        // Use a sequential timer approach for better reliability
        console.log("Setting up mobile navigation sequence");
        
        mobileNavigationTimerRef.current = setTimeout(() => {
          console.log("Executing navigation to:", serviceId);
          try {
            navigate(`/services/${serviceId}`);
            readyToScrollRef.current = true;
            
            // Set a backup timer to reset navigation state if something goes wrong
            setTimeout(() => {
              if (navigationInProgressRef.current) {
                console.log("Navigation safety timeout triggered - resetting navigation state");
                navigationInProgressRef.current = false;
                setIsNavigatingMobile(false);
              }
            }, 3000);
          } catch (error) {
            console.error("Navigation error:", error);
            navigationInProgressRef.current = false;
            setIsNavigatingMobile(false);
          }
        }, 300);
      } else {
        navigate(`/services/${serviceId}`);
        readyToScrollRef.current = true;
      }
      return;
    }
    
    // Already on services page, just update URL and scroll
    console.log(`Already on services page, updating URL to /services/${serviceId}`);
    navigate(`/services/${serviceId}`, { replace: true });
    
    // Add a small delay to ensure the URL change is processed
    setTimeout(() => {
      readyToScrollRef.current = true;
      attemptToScrollToService(serviceId);
    }, 200);
  };

  return (
    <ServiceContext.Provider value={{ activeService, setActiveService, scrollToService, isNavigatingMobile }}>
      {children}
    </ServiceContext.Provider>
  );
};
